import {
  AnyAction,
  Middleware,
  combineReducers,
  configureStore,
} from "@reduxjs/toolkit";

//SLICES
import accountContactSlice from "./slices/accountContactSlice";
import transactionSliceReducer from "./slices/transactionSlice";
import userAccountsReducer from "./slices/userAccountsSlice";
import unifiedBalanceReducer from "./slices/unifiedBalanceSlice";
import echeqReducer from "./slices/echeqSlice";
import activityFiltersSlice from "./slices/activityFiltersSlice";
import ordersFiltersSlice from "./slices/ordersFiltersSlice";
import marketSlice from "./slices/marketSlice";
import operationDataSlice from "./slices/operationDataSlice";
import operationMEPDataSlice from "./slices/operationMEPSlice";
import activityDetailsReducer from "./slices/activityDetailsSlice";
import marketStatusReducer from "./slices/marketStatusSlice";
import depositSlice from "./slices/depositSlice";
import cryptoSlice from "./slices/cryptoSlice";
import appStateReducer from "./slices/appStateSlice";
import termsAndConditionsSlice from "./slices/termsAndConditionsSlice";
import transactionResourcesReducer from "./slices/transactionResourcesSlice";

import authReducer, { logout } from "./slices/authSlice";
import { deleteRefreshTokenSecure } from "../utils/biometric";
import { queryClient } from "../queryClient";
import { ENABLE_AUTH_LOGS } from "../constants/logging";

//instead of defining the reducers in the reducer field of configureStore, combine them here:
const appReducer = combineReducers({
  accountContact: accountContactSlice,
  authentication: authReducer,
  transaction: transactionSliceReducer,
  echeq: echeqReducer,
  userAccounts: userAccountsReducer,
  unifiedBalance: unifiedBalanceReducer,
  activityFilters: activityFiltersSlice,
  ordersFilters: ordersFiltersSlice,
  market: marketSlice,
  operationData: operationDataSlice,
  operationMEPData: operationMEPDataSlice,
  activityDetails: activityDetailsReducer,
  marketStatus: marketStatusReducer,
  deposit: depositSlice,
  crypto: cryptoSlice,
  appState: appStateReducer,
  termsAndConditions: termsAndConditionsSlice,
  transactionResources: transactionResourcesReducer,
});

// Reset the whole store on logout to avoid leaking data between accounts
const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
) => {
  if (action.type === logout.type) {
    // Passing undefined makes each slice return its initial state,
    // while the auth reducer still runs its logout side effects.
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

// Cleanup side effects that live outside Redux state (react-query cache, secure refresh token)
const isActionWithType = (action: unknown): action is AnyAction =>
  typeof (action as AnyAction)?.type === "string";

const logoutCleanupMiddleware: Middleware<{}, ReturnType<typeof rootReducer>> =
  () => (next) => (action) => {
    if (isActionWithType(action) && action.type === logout.type) {
      if (ENABLE_AUTH_LOGS) {
        // Stack trace truncated to top frames so we can identify the
        // call-site without spamming logcat. Sentry already gets full
        // breadcrumbs on its own.
        // eslint-disable-next-line no-console
        console.log("[AUTH-DEBUG] logout action dispatched", {
          stack: new Error().stack?.split("\n").slice(1, 6).join(" | "),
        });
      }
      queryClient.clear();
      deleteRefreshTokenSecure().catch((error) => {
        console.warn("Failed to clear biometric credential on logout", error);
      });
    }
    return next(action);
  };

//then set rootReducer as the reducer object of configureStore
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(logoutCleanupMiddleware),
});

// Get the type of our store variable
export type AppStore = typeof store;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = AppStore["dispatch"];
